import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle, CreditCard, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ResubscribePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(null);
  
  // Get email from navigation state if available
  const email = location.state?.email || '';

  const handleResubscribe = async (priceType) => {
    setLoading(priceType);
    
    try {
      const response = await axios.post(`${API}/api/stripe/guest-checkout`, {
        price_type: priceType,
        success_url: `${window.location.origin}/dashboard?resubscribed=true`,
        cancel_url: `${window.location.origin}/resubscribe?cancelled=true`,
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Unable to start checkout. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Unable to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D063E] to-[#4A0A66] p-4">
      <div className="max-w-lg w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto" />
        </div>
        
        {/* Card */}
        <div 
          className="rounded-3xl p-8 text-center"
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-3">
            Your Subscription Has Ended
          </h1>
          
          {/* Message */}
          <p className="text-gray-300 mb-6 leading-relaxed">
            Your subscription was cancelled and has now expired. To regain access to your journal and all your entries, please resubscribe below.
          </p>
          
          {email && (
            <p className="text-gray-400 text-sm mb-6">
              Account: <span className="text-white">{email}</span>
            </p>
          )}
          
          {/* Pricing Options */}
          <div className="space-y-4 mb-6">
            {/* Monthly Option */}
            <button
              onClick={() => handleResubscribe('monthly')}
              disabled={loading}
              className="w-full p-4 rounded-2xl border border-white/20 hover:border-[#df1283] transition-all group"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-white font-semibold">Monthly Plan</p>
                  <p className="text-gray-400 text-sm">$9.99/month</p>
                </div>
                <div className="flex items-center gap-2">
                  {loading === 'monthly' ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 text-gray-400 group-hover:text-[#df1283] transition-colors" />
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#df1283] transition-colors" />
                    </>
                  )}
                </div>
              </div>
            </button>
            
            {/* Yearly Option */}
            <button
              onClick={() => handleResubscribe('yearly')}
              disabled={loading}
              className="w-full p-4 rounded-2xl border border-[#df1283] hover:border-[#FF7901] transition-all group relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(223, 18, 131, 0.1), rgba(255, 121, 1, 0.1))',
              }}
            >
              <div className="absolute top-0 right-0 bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white text-xs px-3 py-1 rounded-bl-lg font-semibold">
                SAVE 37%
              </div>
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-white font-semibold">Yearly Plan</p>
                  <p className="text-gray-400 text-sm">$75/year <span className="text-gray-500 line-through ml-1">$119.88</span></p>
                </div>
                <div className="flex items-center gap-2">
                  {loading === 'yearly' ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 text-[#df1283] group-hover:text-[#FF7901] transition-colors" />
                      <ArrowRight className="w-5 h-5 text-[#df1283] group-hover:text-[#FF7901] transition-colors" />
                    </>
                  )}
                </div>
              </div>
            </button>
          </div>
          
          {/* Reassurance */}
          <p className="text-gray-500 text-xs mb-4">
            Your previous journal entries are safe and will be restored upon resubscribing.
          </p>
          
          {/* Back to Login Link */}
          <button
            onClick={() => navigate('/login')}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            ← Back to login
          </button>
        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          Questions? Contact us at{" "}
          <a href="mailto:support@unclonedjournal.com" className="text-[#df1283] hover:underline">
            support@unclonedjournal.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default ResubscribePage;
